import CardLayerActions from "@components/CardLayerActions";
import { CheckListIcon } from "@components/Icons";
import { useUserState } from "@context/UserContext";
import {
  TextField,
  createStyles,
  makeStyles,
  useMediaQuery,
  useTheme,
  Typography,
  CircularProgress,
  Button,
} from "@material-ui/core";
import { useFormikContext } from "formik";
import React from "react";
import { Grid } from "@mui/material";
import CalendarRange from "@components/CalendarRange";
import { useReclamationContext } from "@context/ReclamationContext/ReclamationUserContext";
import { IReclamationFormValues } from "./interface";
import DropdownSelectAll from "@components/DropdownSelectAll";

const ReclamationForm: React.FC = () => {
  const classes = useStyles();
  const theme = useTheme();

  const isMobile = useMediaQuery(theme.breakpoints.only("xs"));
  const isTablet = useMediaQuery(theme.breakpoints.only("sm"));
  const userState = useUserState();
  const {
    loadingDataFilterReclamation,
    setRangeDate,
  } = useReclamationContext();

  const {
    values,
    errors,
    touched,
    setFieldValue,
    setFieldTouched,
    submitForm,
  } = useFormikContext<IReclamationFormValues>();

  const userCNPJList = userState.listCNPJ
    .map((ele) => ele.BpID || ele.CNPJ)
    .filter((cnpj): cnpj is string => !!cnpj);

  const cnpjOptions = userState.listCNPJ.map((ele) => ({
    value: ele.BpID ? ele.BpID : ele.CNPJ,
    label: ele.companyName,
  }));

  const handleOnChangeIdReclamation = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setFieldValue("ref_id", event.target.value.replace(/\D/g, ""));
  };

  const isButtonDisabled = !values.cnpj?.length;

  const handleClearFilters = async () => {
    const correctDateRange = [
      new Date(new Date().getFullYear(), new Date().getMonth(), 1),
      new Date(),
    ];

    setFieldValue("ref_id", "");
    setFieldValue("cnpj", userCNPJList);
    setFieldValue("date_range", correctDateRange);
    setRangeDate(correctDateRange);
    submitForm();
  };

  return (
    <Grid container sx={{ border: "0px solid red" }}>
      <Grid item xs={12}>
        <CardLayerActions title="Registros de atendimento" icon={CheckListIcon}>
          <Grid container>
            <Grid
              item
              xs={12}
              md={3}
              style={{
                marginBottom: isMobile || isTablet ? "25px" : "",
                height: "78px",
              }}
            >
              <TextField
                style={{
                  width: isMobile || isTablet ? "100%" : "calc(100% - 30px)",
                }}
                className={classes.textfield}
                label="ID do registro"
                variant="outlined"
                id="ref_id"
                type="text"
                value={values.ref_id}
                onChange={handleOnChangeIdReclamation}
                onBlur={() => setFieldTouched("ref_id", true)}
              />
            </Grid>

            <Grid
              item
              xs={12}
              md={3}
              style={{
                marginBottom: isMobile || isTablet ? "25px" : "",
              }}
            >
              <div
                style={{
                  width: isMobile || isTablet ? "100%" : "calc(100% - 30px)",
                }}
              >
                <DropdownSelectAll
                  options={cnpjOptions}
                  value={values.cnpj}
                  label="Razão social"
                  onChange={(selectedValues) => {
                    setFieldValue("cnpj", selectedValues);
                    setFieldTouched("cnpj", true);
                  }}
                  error={!!errors.cnpj && !!touched.cnpj}
                />
                {isButtonDisabled && (
                  <Typography
                    style={{
                      color: "red",
                      fontSize: "12px",
                      marginTop: "4px",
                    }}
                  >
                    Selecione um CNPJ
                  </Typography>
                )}
              </div>
            </Grid>

            <Grid
              item
              md={3}
              xs={12}
              style={{
                marginBottom: isMobile || isTablet ? "25px" : "",
              }}
            >
              <CalendarRange
                //@ts-ignore
                name="date_range"
                label="Data ou período"
                value={values.date_range}
                onChange={(date_range) => {
                  setFieldValue("date_range", date_range);
                  setFieldTouched("startDate", true);
                  setFieldTouched("endDate", true);
                  setRangeDate(
                    date_range?.map((cont) => (cont ? cont : new Date())) ?? []
                  );
                }}
                onBlur={() => {
                  setFieldTouched("startDate", true);
                  setFieldTouched("endDate", true);
                }}
              />
            </Grid>

            <Grid
              item
              xs={12}
              style={{
                display: "flex",
                justifyContent: "flex-end",
                gap: "10px",
              }}
            >
              <Button
                color="primary"
                variant="outlined"
                onClick={handleClearFilters}
                style={{
                  textTransform: "none",
                  fontWeight: "bold",
                  borderRadius: "2px",
                  boxShadow: "none",
                }}
              >
                Limpar Filtros
              </Button>
              <Button
                disabled={isButtonDisabled}
                color="primary"
                variant="contained"
                onClick={submitForm}
                style={{
                  borderRadius: "2px",
                  textTransform: "none",
                  fontWeight: "bold",
                  background: isButtonDisabled ? "#ccc" : "#D91F05",
                  color: isButtonDisabled ? "#817c7cff" : "#fff",
                  boxShadow: "none",
                }}
                startIcon={
                  loadingDataFilterReclamation && (
                    <CircularProgress
                      color="secondary"
                      style={{ height: "2rem", width: "2rem" }}
                    />
                  )
                }
              >
                {loadingDataFilterReclamation ? "Consultando..." : "Consultar"}
              </Button>
            </Grid>
          </Grid>
        </CardLayerActions>

        <Grid container justifyContent="center">
          <Typography
            style={{
              color: "#3E3D3D",
              fontFamily: "DM Sans",
              fontSize: "16px",
              fontStyle: "normal",
              fontWeight: "400",
              lineHeight: "normal",
              marginTop: "40px",
              marginBottom: "40px",
            }}
          >
            Resultados da pesquisa
          </Typography>
        </Grid>
      </Grid>
    </Grid>
  );
};

export { ReclamationForm };

const useStyles = makeStyles((theme) =>
  createStyles({
    title: {
      color: theme.palette.primary.highlightedplus, //"#3E3D3D",
      fontFamily: "Montserrat",
      fontStyle: "normal",
      fontWeight: "bold",
      lineHeight: "normal",
      paddingTop: "20px",
    },
    inputLabel: {
      position: "absolute",
      color: theme.palette.primary.highlightedplus,
      fontFamily: "Montserrat",
      fontSize: "1.6rem",
      backgroundColor: theme.palette.text.hint,
      paddingLeft: "0.2rem",
      paddingRight: "0.8rem",
      marginLeft: "0rem",
      width: "auto",
    },
    textfield: {
      width: "100%",
      fontFamily: "Montserrat",
      "& label": {
        // Controle de estilo antes de digitar
        color: theme.palette.primary.highlightedplus,
        fontWeight: 400,
        fontSize: "1.6rem",
        borderRadius: "8px",
        opacity: "0.6",
      },
      "& .MuiInputLabel-shrink": {
        // Controle de estilo apos campo digitado
        fontSize: "1.6rem",
        fontWeight: 700,
        color: theme.palette.primary.highlightedplus,
        opacity: 1,
        backgroundColor: theme.palette.text.hint, // "#FFF"
        paddingRight: 6,
      },
      "& .MuiOutlinedInput-notchedOutline": {
        borderRadius: "8px",
        opacity: "0.6",
      },
    },
    itemSelect: {
      display: "flex",
      flexDirection: "column",
      alignItems: "flex-start",
    },
    itemSelectTitle: {
      fontSize: "1.6rem",
      fontWeight: "bold",
    },
    itemSelectSubTitle: {
      fontSize: "1.2rem",
    },
    customSelect: {
      justifyContent: "center",
      alignItems: "center",
      display: "flex",
      marginTop: "0",
      overflow: "hidden",
      whiteSpace: "nowrap",
      textOverflow: "ellipsis",
      fontFamily: "Montserrat",
      fontSize: "16px",
      fontWeight: 400,
      paddingLeft: "14px",
      "& .MuiOutlinedInput-notchedOutline": {
        borderColor: "rgba(114, 101, 81, 0.20)",
      },

      "& .MuiSvgIcon-root": {
        fill: theme.palette.primary.main,
      },

      "&:focus": {
        backgroundColor: theme.palette.text.hint,
      },
      borderRadius: "8px",
    },
    formControl: {
      borderRadius: "8px",
      height: "56px",
      width: "100%",
    },
    gridInput: {
      marginRight: "2rem",
      marginBottom: "2rem",
      height: "50px",
    },
  })
);
